import React from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import BudgetCalculator from './tools/BudgetCalculator';
import TicketFinder from './tools/TicketFinder';

export default function Tools() {
  return (
    <Routes>
      <Route index element={<ToolsHome />} />
      <Route path="budget-calculator" element={<BudgetCalculator />} />
      <Route path="ticket-finder" element={<TicketFinder />} />
    </Routes>
  );
}

function ToolsHome() {
  const tools = [
    {
      id: 'budget-calculator',
      title: 'Travel Budget Calculator',
      description: 'Plan your trip expenses and calculate your total travel budget with daily breakdowns',
      icon: '💰',
      color: 'from-[#4A90E2] to-[#0A2540]',
      path: '/tools/budget-calculator',
      available: true
    },
    {
      id: 'ticket-finder',
      title: 'Ticket Finder',
      description: 'Compare ticket prices from multiple providers and find the best deals on attractions',
      icon: '🎫',
      color: 'from-[#FF6B35] to-[#F7B32B]',
      path: '/tools/ticket-finder',
      available: true
    },
    {
      id: 'best-time',
      title: 'Best Time to Visit',
      description: 'Discover the ideal time to visit your destination based on weather and crowds',
      icon: '📅',
      color: 'from-[#0A2540] to-[#4A90E2]',
      path: '/tools/best-time',
      available: false
    },
    {
      id: 'packing-list',
      title: 'Packing List Generator',
      description: 'Create customized packing lists based on your destination and trip duration',
      icon: '🎒',
      color: 'from-[#F7B32B] to-[#FF6B35]',
      path: '/tools/packing-list',
      available: false
    },
    {
      id: 'transport-planner',
      title: 'Transport Planner',
      description: 'Plan your transportation routes and find the best ways to get around',
      icon: '🚇',
      color: 'from-[#4A90E2] to-[#0A2540]',
      path: '/tools/transport-planner',
      available: false
    },
    {
      id: 'currency-converter',
      title: 'Currency Converter',
      description: 'Convert currencies and calculate exchange rates for your travel budget',
      icon: '💱',
      color: 'from-[#FF6B35] to-[#F7B32B]',
      path: '/tools/currency-converter',
      available: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAFA] to-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-[#0A2540] mb-4">
            Travel Tools
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Smart tools to help you plan, budget, and discover the best deals for your next adventure
          </p>
        </div>

        {/* Tools Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tools.map((tool) => (
            <Link
              key={tool.id}
              to={tool.available ? tool.path : '#'}
              className={`group relative bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 ${
                tool.available ? 'hover:shadow-xl hover:-translate-y-1 cursor-pointer' : 'opacity-75 cursor-not-allowed'
              }`}
            >
              {/* Gradient Header */}
              <div className={`bg-gradient-to-r ${tool.color} p-6 text-white`}>
                <div className="text-5xl mb-3">{tool.icon}</div>
                <h3 className="text-xl font-bold">{tool.title}</h3>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-gray-600 mb-4">{tool.description}</p>
                
                {tool.available ? (
                  <div className="flex items-center text-[#4A90E2] font-semibold group-hover:text-[#FF6B35] transition-colors">
                    <span>Use Tool</span>
                    <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                ) : (
                  <div className="flex items-center text-gray-400">
                    <span className="text-sm">Coming Soon</span>
                  </div>
                )}
              </div>

              {/* Coming Soon Badge */}
              {!tool.available && (
                <div className="absolute top-4 right-4 bg-gray-800 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Coming Soon
                </div>
              )}
            </Link>
          ))}
        </div>

        {/* Info Section */}
        <div className="mt-16 bg-gradient-to-r from-[#0A2540] to-[#1a3a5a] rounded-2xl shadow-xl p-8 md:p-12 text-white">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">Make Travel Planning Easier</h2>
              <p className="text-white/90 mb-6">
                Our suite of travel tools is designed to help you plan smarter, save money, and make the most of your trips. 
                From budget calculators to ticket finders, we've got everything you need in one place.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-[#F7B32B]">✓</span>
                  <span>Free to use</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#F7B32B]">✓</span>
                  <span>No sign-up required</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#F7B32B]">✓</span>
                  <span>Updated daily</span>
                </div>
              </div>
            </div>
            <div className="text-center md:text-right">
              <div className="text-6xl mb-4">🛠️</div>
              <p className="text-white/80 text-sm">More tools coming soon!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
